import React, { useState } from 'react';
import { Send, Phone, Mail, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import emailjs from '@emailjs/browser';

const ContactSection = ({ id }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ type: '', message: '' });

    try {
      // Formspree integration - hardcoded for reliability
      const formspreeID = process.env.REACT_APP_FORMSPREE_ID || 'mdkprzyn';
      const formspreeEndpoint = `https://formspree.io/f/${formspreeID}`;

      console.log('Formspree ID:', formspreeID);
      console.log('Formspree Endpoint:', formspreeEndpoint);

      // Prepare form data for Formspree
      const formPayload = new FormData();
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);
      formPayload.append('company', formData.company);
      formPayload.append('project', formData.project);
      formPayload.append('message', formData.message);
      formPayload.append('_subject', 'Nova mensagem do site Manch Studios');

      // Send to Formspree
      const response = await fetch(formspreeEndpoint, {
        method: 'POST',
        body: formPayload,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        // Success
        setStatus({
          type: 'success',
          message: 'Mensagem enviada com sucesso! Entraremos em contato em breve.'
        });

        alert('✅ Mensagem enviada com sucesso!\n\nObrigado pelo contato! Recebemos sua mensagem e entraremos em contato em breve.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          project: '',
          message: ''
        });
      } else {
        throw new Error('Falha no envio');
      }

    } catch (error) {
      console.error('Form submission error:', error);
      
      setStatus({
        type: 'error',
        message: 'Erro ao enviar mensagem. Tente novamente ou entre em contato diretamente.'
      });

      alert('❌ Erro ao enviar mensagem\n\nTente novamente ou entre em contato diretamente pelo telefone: +55 31 99434-1699');
      
    } finally {
      setIsLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: <Phone size={24} />,
      title: "Telefone",
      value: contactInfo.phone,
      href: `tel:${contactInfo.phone}`
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      value: contactInfo.email,
      href: `mailto:${contactInfo.email}`
    },
    {
      icon: <Globe size={24} />,
      title: "Atendimento",
      value: "Online em todo Brasil",
      href: null
    }
  ];

  return (
    <section id={id} className="section-padding bg-gradient-to-b from-transparent to-bg-card/30">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16 fade-in-up">
          <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm text-brand-primary border border-brand-primary/20 mb-6">
            <span>Entre em Contato</span>
          </div>
          <h2 className="heading-1 mb-6">
            Vamos <span className="text-brand-primary">Conversar</span>?
          </h2>
          <div className="w-20 h-1 bg-brand-primary rounded-full mx-auto mb-8"></div>
          <p className="body-large max-w-3xl mx-auto">
            Pronto para transformar sua ideia em realidade? 
            Entre em contato conosco e descubra como podemos ajudar.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="glass-card p-8">
            <h3 className="heading-2 text-2xl mb-6">
              Solicite um <span className="text-brand-primary">Orçamento</span>
            </h3>
            
            {/* Status Message */}
            {status.message && (
              <div className={`p-4 rounded-lg flex items-center gap-3 mb-6 ${
                status.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {status.type === 'success' ? (
                  <CheckCircle size={20} />
                ) : (
                  <AlertCircle size={20} />
                )}
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Nome *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Seu nome completo"
                    className="bg-bg-card/50 border-border-medium text-white placeholder:text-text-muted focus:border-brand-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="seu@email.com"
                    className="bg-bg-card/50 border-border-medium text-white placeholder:text-text-muted focus:border-brand-primary"
                  />
                </div>
              </div>

              {/* Phone and Company Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Telefone</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    className="bg-bg-card/50 border-border-medium text-white placeholder:text-text-muted focus:border-brand-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-text-secondary">Empresa</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Nome da empresa"
                    className="bg-bg-card/50 border-border-medium text-white placeholder:text-text-muted focus:border-brand-primary"
                  />
                </div>
              </div>

              {/* Project Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Tipo de Projeto *</label>
                <select
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 bg-bg-card/50 border border-border-medium rounded-md text-white focus:border-brand-primary focus:outline-none"
                >
                  <option value="">Selecione o tipo de projeto</option>
                  <option value="design-digital">Design de Produtos Digitais</option>
                  <option value="design-industrial">Design de Produtos Industriais</option>
                  <option value="ux-research">UX Research</option>
                  <option value="prototipagem">Prototipagem</option>
                  <option value="consultoria">Consultoria e Inovação</option>
                  <option value="vr-ar">Experiências Virtuais</option>
                  <option value="outros">Outros</option>
                </select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">Mensagem *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Conte-nos sobre seu projeto, objetivos e expectativas..."
                  className="bg-bg-card/50 border-border-medium text-white placeholder:text-text-muted focus:border-brand-primary resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-text-inverse border-t-transparent"></div>
                ) : (
                  <Send size={16} />
                )}
                {isLoading ? 'Enviando...' : 'Enviar Mensagem'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Contact Methods */}
            <div className="glass-card p-8">
              <h3 className="heading-2 text-2xl mb-6">
                Outras formas de <span className="text-brand-primary">contato</span>
              </h3>
              
              <div className="space-y-6">
                {contactMethods.map((method, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-bg-card/30 hover:bg-bg-card/50 transition-colors">
                    <div className="w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center border border-brand-primary/20">
                      <div className="text-brand-primary">
                        {method.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">{method.title}</h4>
                      {method.href ? (
                        <a 
                          href={method.href}
                          className="text-text-secondary hover:text-brand-primary transition-colors"
                        >
                          {method.value}
                        </a>
                      ) : (
                        <span className="text-text-secondary">{method.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="glass-card p-8">
              <h3 className="heading-3 text-xl mb-6">Por que nos escolher?</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Experiência Comprovada</h4>
                    <p className="text-text-secondary text-sm">Mais de 5 anos criando soluções inovadoras</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Atendimento Personalizado</h4>
                    <p className="text-text-secondary text-sm">Cada projeto recebe atenção dedicada e customizada</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Metodologia Comprovada</h4>
                    <p className="text-text-secondary text-sm">Design Thinking e Human-Centered Design</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Suporte Completo</h4>
                    <p className="text-text-secondary text-sm">Do conceito à implementação e além</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;